import { useEffect, useState } from 'react'

import { Button } from '@/components/ui/button.tsx'
import { Dialog } from '@/components/ui/dialog.tsx'
import type { CreatorFeatures } from '@/modules/accounts/types.ts'
import type { Creator } from '@/modules/creators/types.ts'
import type { BillingPlan } from '@/modules/licenses/types.ts'
import { classNames } from '@/utils/helpers'

interface UpgradeRequiredDialogProps {
  creator: Creator
  plans: BillingPlan[]
  requiredFeature: string
  featureName: string
  openUpgradeLicense: (creator: Creator, plan: BillingPlan) => void
  isUpgradeAvailable: boolean
}

export const UpgradeRequiredDialog = ({
  creator,
  plans,
  requiredFeature,
  featureName,
  openUpgradeLicense,
  isUpgradeAvailable,
}: UpgradeRequiredDialogProps) => {
  const requiredPlan = getRouteMinPlan(plans, requiredFeature)
  const [open, setOpen] = useState(false)

  useEffect(() => {
    // Trigger rerender, because the animation doesn't play on first render
    setOpen(true)
  }, [])

  return (
    requiredPlan && (
      <Dialog
        show={open}
        onClose={() => setOpen(false)}
      >
        <Dialog.Header>
          <Dialog.Title className="mb-2">Upgrade required</Dialog.Title>
        </Dialog.Header>
        <Dialog.Description>
          <p className="text-center">
            To use <span className="text-title">{featureName}</span>, you will
            need to upgrade <span className="text-title">{creator.name}</span>
            &#39;s license to{' '}
            <span className="text-title">{requiredPlan.displayName}</span>.
          </p>
        </Dialog.Description>
        <Dialog.ClosableFooter
          onClose={() => {
            setOpen(false)
            openUpgradeLicense(creator, requiredPlan)
          }}
          closeText="Got it"
          className={classNames({
            'grid-cols-1': !isUpgradeAvailable,
          })}
        >
          {isUpgradeAvailable && (
            <Button
              onClick={() => {
                setOpen(false)
                openUpgradeLicense(creator, requiredPlan)
              }}
            >
              Upgrade Now
            </Button>
          )}
        </Dialog.ClosableFooter>
      </Dialog>
    )
  )
}

const getRouteMinPlan = (plans: BillingPlan[], feature: string) => {
  return plans
    .filter((plan) => plan.specialFeatures.includes(feature as CreatorFeatures))
    .sort((a, b) => a.level - b.level)[0]
}
